import { CaretDown, SlidersHorizontal } from '@phosphor-icons/react'
import type { NumericTolerances } from '../../types'
import type { ComparisonSettings } from '../../ui/settings'

interface AdvancedSettingsProps {
  onChange: (settings: ComparisonSettings) => void
  settings: ComparisonSettings
}

function toFiniteNumber(value: string, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function AdvancedSettings({ onChange, settings }: AdvancedSettingsProps) {
  const setTolerance = (field: keyof NumericTolerances, value: string): void => {
    onChange({
      ...settings,
      tolerances: {
        ...settings.tolerances,
        [field]: Math.max(0, toFiniteNumber(value, settings.tolerances[field])),
      },
    })
  }

  return (
    <details className="advanced-settings">
      <summary>
        <span>
          <SlidersHorizontal aria-hidden="true" />
          高级设置
        </span>
        <CaretDown aria-hidden="true" className="advanced-settings__caret" />
      </summary>
      <div className="advanced-settings__content">
        <fieldset>
          <legend>数值容差</legend>
          <label>
            <span>工程量</span>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={settings.tolerances.quantity}
              onChange={(event) => setTolerance('quantity', event.target.value)}
            />
          </label>
          <label>
            <span>综合单价（元）</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.tolerances.unitPrice}
              onChange={(event) => setTolerance('unitPrice', event.target.value)}
            />
          </label>
          <label>
            <span>合价（元）</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.tolerances.totalPrice}
              onChange={(event) => setTolerance('totalPrice', event.target.value)}
            />
          </label>
        </fieldset>

        <fieldset>
          <legend>候选匹配</legend>
          <label>
            <span>高可信阈值</span>
            <input
              type="number"
              min="0.5"
              max="1"
              step="0.01"
              value={settings.highConfidenceThreshold}
              onChange={(event) => {
                const highConfidenceThreshold = Math.min(
                  1,
                  Math.max(0.5, toFiniteNumber(event.target.value, 0.88)),
                )
                onChange({
                  ...settings,
                  highConfidenceThreshold,
                  pendingThreshold: Math.min(highConfidenceThreshold, settings.pendingThreshold),
                })
              }}
            />
          </label>
          <label>
            <span>待确认阈值</span>
            <input
              type="number"
              min="0.3"
              max="1"
              step="0.01"
              value={settings.pendingThreshold}
              onChange={(event) =>
                onChange({
                  ...settings,
                  pendingThreshold: Math.min(
                    settings.highConfidenceThreshold,
                    Math.min(1, Math.max(0.3, toFiniteNumber(event.target.value, 0.72))),
                  ),
                })
              }
            />
          </label>
          <label>
            <span>近似候选差值</span>
            <input
              type="number"
              min="0"
              max="0.2"
              step="0.01"
              value={settings.ambiguityMargin}
              onChange={(event) =>
                onChange({
                  ...settings,
                  ambiguityMargin: Math.min(
                    0.2,
                    Math.max(0, toFiniteNumber(event.target.value, 0.03)),
                  ),
                })
              }
            />
          </label>
          <label>
            <span>每行候选上限</span>
            <input
              type="number"
              min="1"
              max="50"
              step="1"
              value={settings.maxCandidates}
              onChange={(event) =>
                onChange({
                  ...settings,
                  maxCandidates: Math.min(
                    50,
                    Math.max(1, Math.round(toFiniteNumber(event.target.value, 50))),
                  ),
                })
              }
            />
          </label>
        </fieldset>
      </div>
    </details>
  )
}
